import React from 'react';
import styled, { keyframes } from 'styled-components';
import Button from 'components/button';
import Icon from 'components/icon';
import SkeletonShape from 'components/skeleton-shape';
import { STATUS } from 'utils/imageLoader';
import useImage from './use-image';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Stage = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  touch-action: none;
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 4px;
    animation: ${fadeIn} 0.25s ease;
    will-change: transform;
    @media (max-width: 835px) {
      border-radius: 0px;
    }
  }
`;

const Loader = styled(SkeletonShape)`
  width: min(70%, 900px);
  height: 70%;
  border-radius: 8px;
`;

const ErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.7);
  i {
    font-size: 40px;
  }
  button {
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 20px;
    padding: 6px 20px;
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
`;

const ZoomControls = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border-radius: 20px;
  background-color: rgba(0, 0, 0, 0.55);
  z-index: 2;
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: #fff;
    font-size: 14px;
    transition: background-color 0.2s ease;
    &:hover {
      background-color: rgba(255, 255, 255, 0.15);
    }
  }
  .zoomLevel {
    min-width: 48px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
  }
`;

export default ({ url, alt, zoom }) => {
  const { status, retry } = useImage(url, { priority: 100 });

  return (
    <Stage ref={zoom.containerRef} {...zoom.handlers}>
      {status === STATUS.error ? (
        <ErrorBox>
          <Icon name="fa-light fa-image-slash" />
          <div>This image could not be loaded</div>
          <Button onClick={retry}>Retry</Button>
        </ErrorBox>
      ) : status === STATUS.loaded ? (
        <img key={url} src={url} alt={alt} style={zoom.imageStyle} draggable={false} />
      ) : (
        <Loader background="#2a2a2a" />
      )}
      {status === STATUS.loaded && (
        <ZoomControls>
          <Button onClick={zoom.zoomOut} aria-label="Zoom out" disabled={!zoom.isZoomed}>
            <Icon name="fa-solid fa-magnifying-glass-minus" />
          </Button>
          <Button className="zoomLevel" onClick={zoom.reset} aria-label="Reset zoom">
            {Math.round(zoom.scale * 100)}%
          </Button>
          <Button onClick={zoom.zoomIn} aria-label="Zoom in">
            <Icon name="fa-solid fa-magnifying-glass-plus" />
          </Button>
        </ZoomControls>
      )}
    </Stage>
  );
};
